











import { SfLoader } from '@storefront-ui/vue';
import { defineComponent, onMounted, ref, useContext, useRoute, useRouter, useFetch } from '@nuxtjs/composition-api';
import { useUrlResolver } from '~/composables/useUrlResolver';
import TrustedShopsBadge from '~/components/TrustedShopsBadge.vue';

import { useUrlData } from '~/stores/urldata';
import type { MetaDataInterface, CustomPageMeta } from '~/customQueries/metaQueries/types';
import { htmlEntitiesDecoder } from '~/helpers/htmlEntitiesDecoder';
import { Logger } from '~/helpers/logger';

export default defineComponent({
  name: 'RoutePage',
  components: {
    SfLoader,
    CategoryPage: () => import('~/modules/catalog/pages/category.vue'),
    ProductPage: () => import('~/modules/catalog/pages/product.vue'),
    Page: () => import('~/pages/Page.vue'),
    Home: () => import('~/pages/Home.vue'),
    TrustedShopsBadge,
  },

  setup() {
    const route = useRoute();
    const { params } = route.value;
    const { error: nuxtError, app, redirect } = useContext();
    const urlData = useUrlData();
    const router = useRouter();

    const { getUrlData, getUrlMetaData, getCanonicalUrl } = useUrlResolver();

    const pageType = ref('');
    const loadingData = ref(false);

    const pageMeta = ref<CustomPageMeta>({
      title: 'PapierFischer OnlineStore - PapierFischer Shop',
      description:
        'Füller, Kugelschreiber, Schulranzen, Accessoires vieler Marken online kaufen. Lieferung innerhalb 1- 2 Tagen.',
      keywords: '',
      robots: '',
      product_schema: '',
      website: '',
      seller: '',
      webpage: '',
      canonicalUrl: '', // set via i18n module
    });

    const isProductPage = (type = pageType.value) => {
      return type == 'PRODUCT';
    };
    const isCategoryPage = (type = pageType.value) => {
      return type == 'CATEGORY';
    };
    const isCmsPage = (type = pageType.value) => {
      return type == 'CMS_PAGE';
    };
    const isCategoryFilterPage = (type = pageType.value) => {
      return type == 'CATEGORY_FILTER';
    };
    const isHomePage = (type = pageType.value) => {
      return type == 'Home';
    };

    useFetch(async () => {
      try {
        loadingData.value = true;
        console.log('process.client', process.client);
        console.log('process.server', process.server);
        if (!params?.slug) {
          pageType.value = 'Home';
        } else {
          const resolvedUrl = await getUrlData(params.slug);

          console.log('resolvedUrl', resolvedUrl);
          if (resolvedUrl) {
            if (resolvedUrl?.redirect_code == 301 || resolvedUrl?.redirect_code == 302) {
              let fullPath = resolvedUrl?.relative_url
                ? app.localeRoute({
                    name: 'route-page',
                    params: { slug: resolvedUrl?.relative_url },
                  }).fullPath
                : app.localeRoute({ name: 'home' }).fullPath;
              if (
                (isProductPage(resolvedUrl?.type) ||
                  isCategoryPage(resolvedUrl?.type) ||
                  isCategoryFilterPage(resolvedUrl?.type)) &&
                !fullPath.startsWith('/shop/')
              ) {
                fullPath = `/shop${fullPath}`;
              }
              redirect(301, decodeURIComponent(fullPath));

              return;
            } else {
              urlData.setResolverData(resolvedUrl);
              pageType.value = resolvedUrl?.type || '';
            }
          }
        }

        // Redirect product and category pages to the /shop/ url and all others to the version without /shop/
        if ((isProductPage() || isCategoryPage() || isCategoryFilterPage()) && !route.value.path.startsWith('/shop/')) {
          redirect(`/shop${route.value.path}`);
          return;
        } else if (
          !(isProductPage() || isCategoryPage() || isCategoryFilterPage()) &&
          route.value.path.startsWith('/shop/')
        ) {
          redirect(route.value.path.replace('/shop', ''));
          return;
        }

        let id = '';
        let type = pageType.value;

        if (isHomePage()) {
          id = 'home';
          type = 'CMS_PAGE';
        } else if (isProductPage()) {
          id = urlData.resolverUrl.sku;
        } else if (isCategoryPage() || isCategoryFilterPage()) {
          id = urlData.resolverUrl.uid;
        } else if (isCmsPage()) {
          id = urlData.resolverUrl.identifier;
        }

        // get meta data
        if (id) {
          let data: MetaDataInterface | null = await getUrlMetaData(type, id);

          if (data) {
            pageMeta.value.title = htmlEntitiesDecoder(data.meta_title || pageMeta.value.title);
            console.log('pageMeta.value.title', pageMeta.value.title);
            pageMeta.value.description = htmlEntitiesDecoder(data.meta_description || pageMeta.value.description);
            pageMeta.value.keywords = htmlEntitiesDecoder(
              data.meta_keywords || data.meta_keyword || pageMeta.value.keywords,
            );
            pageMeta.value.product_schema = data.mw_seo_markup?.rich_snippets.product || '';
            pageMeta.value.website = data.mw_seo_markup?.rich_snippets.website || '';
            pageMeta.value.seller = data.mw_seo_markup?.rich_snippets.seller || '';
            pageMeta.value.webpage = data.mw_seo_markup?.rich_snippets.webpage || '';
            pageMeta.value.canonicalUrl = getCanonicalUrl(
              data.mw_canonical_url?.url || data.canonical_url || '',
              pageType.value,
            );
          }
        }
      } catch (e) {
        console.error('Error: ', e);
      } finally {
        // loadingData.value = false;
      }
    });

    onMounted(() => {
      loadingData.value = false;

      if (window?.scroll) {
        window.scroll(0, 0);
      }
    });

    const getRenderComponent = () => {
      if (isHomePage()) {
        return 'Home';
      }
      if (isProductPage()) {
        return 'ProductPage';
      }
      if (isCategoryPage() || isCategoryFilterPage()) {
        return 'CategoryPage';
      }
      if (isCmsPage()) {
        return 'Page';
      }
      return 'div';
    };

    return {
      pageMeta,
      loadingData,
      getRenderComponent,
      pageType,
      isCmsPage,
      isProductPage,
      isCategoryPage,
    };
  },

  head() {
    const scriptTags = [];
    return {
      // @ts-ignore
      title: htmlEntitiesDecoder(this.pageMeta?.title || ''),
      meta: [
        {
          hid: 'og:title',
          name: 'og:title',
          property: 'og:title',
          // @ts-ignore
          content: htmlEntitiesDecoder(this.pageMeta?.title || ''),
        },
        {
          hid: 'description',
          name: 'description',
          // @ts-ignore
          content: this.pageMeta?.description || '',
        },
        {
          hid: 'og:description',
          name: 'og:description',
          property: 'og:description',
          // @ts-ignore
          content: this.pageMeta?.description || '',
        },
        {
          hid: 'keywords',
          name: 'keywords',
          // @ts-ignore
          content: this.pageMeta?.keywords || '',
        },
        {
          hid: 'robots',
          name: 'robots',
          // @ts-ignore
          content: this.pageMeta?.robots || 'INDEX, FOLLOW',
        },
      ],
      link: [
        {
          hid: 'canonical',
          rel: 'canonical',
          // @ts-ignore
          href: this.pageMeta?.canonicalUrl || '',
        },
      ],
      script: scriptTags,
    };
  },
});
